/**
 * The app's sounds: notifications (a chat message, a meeting about to start, a
 * task for you, someone knocking on a Firmio Meet) and the voice-room cues
 * (someone joining or leaving).
 *
 * They are short recordings (CC0, see public/sounds/LICENSE.txt) in
 * public/sounds/notify/, fetched once, decoded with the Web Audio API and
 * cached; {@link armSounds} pre-warms them. Should a file fail to load (an
 * offline PWA, a blocked request) the old synthesised notes play instead, so
 * a sound is never silently missed.
 *
 * A push notification's sound is the operating system's and cannot be
 * replaced from a web page (the Notifications API `sound` property is
 * implemented by nobody). While a Firmio tab is open the service worker hands
 * the push to the page, which plays the matching sound here instead
 * (lib/pushSounds.ts).
 *
 * **Browsers refuse to play audio until the person has interacted with the
 * page.** {@link armSounds} hooks the first click or key press to unlock the
 * context, so the first message of a session is not silently swallowed.
 */

export type SoundName = 'message' | 'join' | 'leave' | 'meeting' | 'task' | 'knock';

export const SOUND_NAMES: readonly SoundName[] = [
  'message',
  'join',
  'leave',
  'meeting',
  'task',
  'knock',
];

/** Per-device, not per-account: this is about the speakers in front of you. */
const STORAGE_KEY = 'firmio.sounds';

const FILES: Record<SoundName, string> = {
  message: '/sounds/notify/chat.mp3',
  join: '/sounds/notify/join.mp3',
  leave: '/sounds/notify/leave.mp3',
  meeting: '/sounds/notify/meeting.mp3',
  task: '/sounds/notify/task.mp3',
  knock: '/sounds/notify/knock.mp3',
};

/**
 * Playback level per sound. The files peak at -3 dBFS; these keep the
 * frequent ones soft (heard dozens of times a day by someone already looking)
 * and the knock clearly audible (someone is waiting at the door).
 */
const GAIN: Record<SoundName, number> = {
  message: 0.45,
  join: 0.4,
  leave: 0.4,
  meeting: 0.55,
  task: 0.5,
  knock: 0.85,
};

let context: AudioContext | null = null;
let armed = false;

type AudioContextCtor = typeof AudioContext;

function audioContextCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as Window & { webkitAudioContext?: AudioContextCtor };
  return window.AudioContext ?? w.webkitAudioContext ?? null;
}

function getContext(): AudioContext | null {
  const Ctor = audioContextCtor();
  if (!Ctor) return null;
  if (!context) {
    try {
      context = new Ctor();
    } catch {
      return null;
    }
  }
  return context;
}

/** Whether sounds are switched on for this browser. Defaults to on. */
export function soundsEnabled(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== 'off';
  } catch {
    // Private mode, blocked site data: play rather than go mysteriously silent.
    return true;
  }
}

export function setSoundsEnabled(enabled: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, enabled ? 'on' : 'off');
  } catch {
    /* Nothing to do; the setting simply will not survive a reload. */
  }
}

// ---------------------------------------------------------------------------
// Recordings
// ---------------------------------------------------------------------------

const decoded = new Map<SoundName, AudioBuffer>();
const loading = new Map<SoundName, Promise<AudioBuffer | null>>();

function loadSample(ctx: AudioContext, name: SoundName): Promise<AudioBuffer | null> {
  const cached = decoded.get(name);
  if (cached) return Promise.resolve(cached);
  const pending = loading.get(name);
  if (pending) return pending;
  const promise = (async () => {
    try {
      const res = await fetch(FILES[name]);
      if (!res.ok) return null;
      const buffer = await ctx.decodeAudioData(await res.arrayBuffer());
      decoded.set(name, buffer);
      return buffer;
    } catch {
      return null;
    } finally {
      loading.delete(name);
    }
  })();
  loading.set(name, promise);
  return promise;
}

function playBuffer(ctx: AudioContext, buffer: AudioBuffer, gainValue: number): void {
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(gainValue, ctx.currentTime);
  src.connect(gain).connect(ctx.destination);
  src.start();
}

/**
 * Unlock audio on the first real interaction, then pre-warm the recordings.
 *
 * Every browser suspends an AudioContext created without a user gesture, and a
 * suspended context plays nothing while reporting success. Call once at
 * startup; the listeners remove themselves after the first event.
 */
export function armSounds(): void {
  if (armed || typeof window === 'undefined') return;
  armed = true;

  const unlock = () => {
    const ctx = getContext();
    if (ctx?.state === 'suspended') void ctx.resume().catch(() => {});
    if (ctx) for (const name of SOUND_NAMES) void loadSample(ctx, name);
    window.removeEventListener('pointerdown', unlock);
    window.removeEventListener('keydown', unlock);
  };
  window.addEventListener('pointerdown', unlock, { once: true });
  window.addEventListener('keydown', unlock, { once: true });
}

// ---------------------------------------------------------------------------
// Synthesised fallback (the original notes)
// ---------------------------------------------------------------------------

interface Note {
  /** Hz. */
  frequency: number;
  /** Seconds from the start of the sound. */
  at: number;
  /** Seconds. */
  duration: number;
  /** Peak gain, 0–1. */
  gain: number;
}

const FALLBACK: Record<SoundName, Note[]> = {
  // Two clean notes a fourth apart, falling: "something arrived".
  message: [
    { frequency: 880, at: 0, duration: 0.09, gain: 0.16 },
    { frequency: 660, at: 0.08, duration: 0.13, gain: 0.13 },
  ],
  // Rising: someone arrived.
  join: [
    { frequency: 523.25, at: 0, duration: 0.08, gain: 0.12 },
    { frequency: 784, at: 0.07, duration: 0.14, gain: 0.12 },
  ],
  leave: [
    { frequency: 784, at: 0, duration: 0.08, gain: 0.12 },
    { frequency: 523.25, at: 0.07, duration: 0.14, gain: 0.12 },
  ],
  meeting: [
    { frequency: 659.25, at: 0, duration: 0.12, gain: 0.14 },
    { frequency: 783.99, at: 0.11, duration: 0.12, gain: 0.14 },
    { frequency: 1046.5, at: 0.22, duration: 0.24, gain: 0.14 },
  ],
  task: [
    { frequency: 783.99, at: 0, duration: 0.1, gain: 0.14 },
    { frequency: 1174.66, at: 0.09, duration: 0.2, gain: 0.13 },
  ],
  knock: [
    { frequency: 220, at: 0, duration: 0.07, gain: 0.3 },
    { frequency: 220, at: 0.16, duration: 0.07, gain: 0.3 },
  ],
};

function playNotes(ctx: AudioContext, notes: Note[]): void {
  const start = ctx.currentTime;
  for (const note of notes) {
    try {
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = note.frequency;
      const from = start + note.at;
      const to = from + note.duration;
      // Ramped, never switched: an abrupt start or stop clicks.
      gain.gain.setValueAtTime(0.0001, from);
      gain.gain.exponentialRampToValueAtTime(note.gain, from + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, to);
      oscillator.connect(gain).connect(ctx.destination);
      oscillator.start(from);
      oscillator.stop(to + 0.02);
    } catch {
      /* One note failing is not worth taking the rest of the sound down. */
    }
  }
}

// ---------------------------------------------------------------------------
// Playing
// ---------------------------------------------------------------------------

/**
 * Whether a sound asked for now would actually be heard: sounds on, Web Audio
 * available and already unlocked by an interaction (lib/pushSounds.ts uses it
 * to decide whether the system sound can stay quiet).
 */
export function canPlayNow(): boolean {
  if (!soundsEnabled()) return false;
  return context?.state === 'running';
}

const lastPlayed = new Map<SoundName, number>();

/** Milliseconds since `name` last played on this page (Infinity if never). */
export function msSinceLastPlayed(name: SoundName): number {
  const at = lastPlayed.get(name);
  return at === undefined ? Number.POSITIVE_INFINITY : Date.now() - at;
}

/**
 * Play a sound regardless of the on/off switch — the voice-room cues, which
 * confirm your own actions in a call (lib/voiceSounds.ts). Best-effort.
 */
export function playCue(name: SoundName): void {
  try {
    const ctx = getContext();
    if (!ctx) return;
    // A context suspended by autoplay policy: try to resume, and let this one
    // go unheard rather than queue a burst of sounds for later.
    if (ctx.state === 'suspended') {
      void ctx.resume().catch(() => {});
      return;
    }
    lastPlayed.set(name, Date.now());
    const cached = decoded.get(name);
    if (cached) {
      playBuffer(ctx, cached, GAIN[name]);
      return;
    }
    void loadSample(ctx, name).then((buffer) => {
      if (buffer) playBuffer(ctx, buffer, GAIN[name]);
      else playNotes(ctx, FALLBACK[name]);
    });
  } catch {
    /* Audio must never break the page. */
  }
}

/**
 * Play one of the app's notification sounds. Silent — without throwing — when
 * sounds are off, when the browser has no Web Audio, or when the context is
 * still locked.
 */
export function playSound(name: SoundName): void {
  if (!soundsEnabled()) return;
  playCue(name);
}
